// frontend/src/core/auth/impersonation.ts
//
// Client-side helpers for the admin "enter impersonation" flow. decodeImp is
// DISPLAY-ONLY — it never verifies the JWT signature; the server remains the
// sole authority on whether the token is valid. stashAdminToken /
// takeStashedAdminToken round-trip the admin's own session token through
// sessionStorage while an impersonation session is active, so the app can
// restore it when the admin exits impersonation.

const STASH_KEY = 'worqly.imp.admin-token';

export interface ImpClaims {
  admin_id: string;
  admin_label: string;
  exp: number;
}

/**
 * Base64url-decode a JWT's payload and pull out the impersonation claim
 * (`imp: { admin_id, admin_label }`) plus the token's own `exp`. Returns null
 * on any parse failure or when the token carries no `imp` claim — this is a
 * display helper, not a verifier.
 */
export function decodeImp(token: string): ImpClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (!payload || typeof payload !== 'object') return null;
    const imp = payload.imp;
    if (!imp || typeof imp !== 'object') return null;
    const { admin_id, admin_label } = imp;
    const exp = payload.exp;
    if (typeof admin_id !== 'string' || typeof admin_label !== 'string' || typeof exp !== 'number') {
      return null;
    }
    return { admin_id, admin_label, exp };
  } catch {
    return null;
  }
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const withPadding = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  return decodeURIComponent(
    atob(withPadding)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join(''),
  );
}

/** Stash the admin's own session token while an impersonation session runs. */
export function stashAdminToken(token: string): void {
  sessionStorage.setItem(STASH_KEY, token);
}

/** Retrieve and clear the stashed admin token, or null if none is stashed. */
export function takeStashedAdminToken(): string | null {
  const token = sessionStorage.getItem(STASH_KEY);
  sessionStorage.removeItem(STASH_KEY);
  return token;
}

/**
 * Clear any stashed admin token without reading it. Used by logout() and the
 * 401 handling path so a stale admin session token can never survive past the
 * session that stashed it (e.g. the admin logs out entirely mid-impersonation,
 * or the impersonated session is force-ended by the server).
 */
export function clearStashedAdminToken(): void {
  sessionStorage.removeItem(STASH_KEY);
}
